import { CalendarIcon, GitBranchIcon, ListTreeIcon } from "lucide-react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { formatWorkItemId } from "~/lib/formatWorkItemId";
import { cn } from "~/lib/utils";
import { PriorityIcon } from "~/components/projects/ProjectsPriorityIcon";
import { deriveTaskPresentation } from "./taskPresentation";
import { deriveSubtaskProgress } from "./subtaskProgress";
import { AgentStatusBadge } from "./atoms/AgentStatusBadge";
import { SubtaskProgress } from "./atoms/SubtaskProgress";
import { TaskAssigneeCluster } from "./atoms/TaskCardOwnership";
import { TaskStatusGlyph } from "./atoms/TaskStatusGlyph";
import { taskWorkItemTypeLabel } from "./atoms/TaskWorkItemTypeIcon";

export interface ProjectTaskRowProps {
  readonly task: CmmdBoardTask;
  readonly threadIndex: ReadonlyMap<string, SidebarThreadSummary>;
  readonly pending: boolean;
  readonly hasConversation: boolean;
  readonly onOpenTask: (taskId: number) => void;
  readonly onOpenThread?: ((taskId: number) => void) | undefined;
  readonly workItemPrefix?: string | null | undefined;
}

/** One task as a Linear-style list row. Click opens the detail drawer. */
export function ProjectTaskRow({
  task,
  threadIndex,
  pending,
  hasConversation,
  onOpenTask,
  onOpenThread,
  workItemPrefix,
}: ProjectTaskRowProps) {
  const p = deriveTaskPresentation(task, threadIndex);
  const progress = deriveSubtaskProgress(task);
  const workItemId = formatWorkItemId(workItemPrefix, task.id);

  return (
    <div
      className={cn(
        "flex w-full items-center gap-2 px-3 py-1.5 transition-colors duration-150 hover:bg-muted md:py-1",
        pending && "opacity-60",
      )}
    >
      {/* The title is the row's open-task control; action controls (agent
          badge) are siblings, never nested inside it (no button-in-button). */}
      <button
        type="button"
        onClick={() => onOpenTask(task.id)}
        aria-label={`Open ${taskWorkItemTypeLabel(task.workItemType)} task ${workItemId}: ${task.title}`}
        // The row title is the primary way a task is opened, so it carries the
        // full touch minimum on coarse pointers. Released at `md`, where the
        // dense desktop row height is the intended affordance.
        className="flex min-h-touch min-w-0 flex-1 items-center gap-1.5 text-left text-read text-foreground/90 md:min-h-0 md:text-sm"
      >
        <PriorityIcon priority={task.priority} />
        <span className="shrink-0 font-mono text-meta tabular-nums text-muted-foreground md:text-[11px]">
          {workItemId}
        </span>
        <TaskStatusGlyph status={task.status} />
        <span className="truncate">{task.title}</span>
      </button>

      {p.isBlocked ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-meta text-hue-rose-foreground md:text-[11px]">
          Blocked
        </span>
      ) : null}
      {progress != null ? (
        <SubtaskProgress progress={progress} />
      ) : p.subtaskCount > 0 ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-meta text-muted-foreground md:text-[11px]">
          <ListTreeIcon className="size-3" aria-hidden />
          {p.subtaskCount}
        </span>
      ) : null}
      {p.resolvedThread ? (
        <AgentStatusBadge
          agentStatus={p.resolvedThread.agentStatus}
          agentProvider={p.resolvedThread.agentProvider}
          onClick={onOpenThread ? () => onOpenThread(task.id) : undefined}
          className="shrink-0"
        />
      ) : hasConversation || task.threadId !== null ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-meta text-muted-foreground md:text-[11px]">
          <GitBranchIcon className="size-3" aria-hidden />
          History
        </span>
      ) : null}
      {p.dueLabel ? (
        <span className="inline-flex shrink-0 items-center gap-1 font-mono text-meta tabular-nums text-muted-foreground/80 md:text-[11px]">
          <CalendarIcon className="size-3" aria-hidden />
          {p.dueLabel}
        </span>
      ) : null}
      {/* Same assignee cluster as the board card and the desktop table row.
          Owner is deliberately absent: this row is already at its width budget
          on a phone, and accountability is a detail-rail question, not a
          scanning one. */}
      <TaskAssigneeCluster task={task} />
    </div>
  );
}
